// src/components/ResultsTable.js
import React from "react";

const COLUMNS = [
  { key: "status",       label: "STATUS",        noSort: true },
  { key: "programName",  label: "PROGRAM NAME" },
  { key: "practice",     label: "PRACTICE" },
  { key: "solutionType", label: "SOLUTION TYPE" },
  { key: "expiry",       label: "EXPIRY" },
  { key: "documents",    label: "DOCUMENTS",     noSort: true },
];

const PRACTICE_COLOR = {
  "Data & AI":       "text--teal",
  "Cloud & Digital": "text--green",
  "TL&M":            "text--purple",
};

const SOLUTION_COLOR = {
  "Deep Skilling":   "text--blue",
  "Transformative":  "text--orange",
  "BootCamp":        "text--bootcamp",
};

function isRecentlyUpdated(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d)) return false;
  return (new Date() - d) / 86400000 <= 30;
}

function SortIcon({ active, dir }) {
  if (!active) return <span className="sort-icon sort-icon--inactive">↕</span>;
  return <span className="sort-icon sort-icon--active">{dir === "asc" ? "↑" : "↓"}</span>;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.round((d.setHours(0,0,0,0) - new Date().setHours(0,0,0,0)) / 86400000);
}

function formatDDMMYYYY(date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${date.getFullYear()}`;
}

function ExpiryCell({ value }) {
  const days = daysUntil(value);
  if (days === null) return <span className="cell-na">-</span>;
  let cls = "expiry--green";
  if (days < 0 || days <= 7) cls = "expiry--red";
  else if (days <= 30)        cls = "expiry--amber";
  return <span className={`expiry-text ${cls}`}>{formatDDMMYYYY(new Date(value))}</span>;
}

function DocumentLinks({ coverEmail, approachNote, successStory, discoveryQuestions, modified }) {
  const updated = isRecentlyUpdated(modified);
  const docs = [
    { url: coverEmail,         label: "Cover Email" },
    { url: approachNote,       label: "Product Note" },
    { url: successStory,       label: "Success Story" },
    { url: discoveryQuestions, label: "Discovery Questions" },
  ].filter((d) => d.url);

  if (!docs.length) return <span className="cell-empty">—</span>;

  return (
    <div className="doc-links">
      {docs.map(({ url, label }) => (
        <a key={label} href={url} className="doc-link" target="_blank" rel="noopener noreferrer">
          ↓ {label}
          {updated && <span className="badge-updated">Updated</span>}
        </a>
      ))}
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr className="skeleton-row">
      {COLUMNS.map((c) => <td key={c.key}><div className="skeleton-cell" /></td>)}
    </tr>
  );
}

export function ResultsTable({ courses, loading, sortBy, sortDir, onSort, sortOptions, selectedSort, onSortChange }) {
  return (
    <section className="results-section">
      <div className="results-header">
        <span className="results-count">
          Showing <strong>{loading ? "…" : courses.length}</strong> results
        </span>
        <label className="sort-label">
          Sort by:
          <select className="sort-select" value={selectedSort} onChange={(e) => onSortChange(e.target.value)}>
            {sortOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      <div className="table-wrap">
        <table className="results-table" aria-label="Solutions catalogue">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`th ${col.noSort ? "" : "th--sortable"} ${sortBy === col.key ? "th--sorted" : ""}`}
                  onClick={col.noSort ? undefined : () => onSort(col.key)}
                >
                  {col.label}
                  {!col.noSort && <SortIcon active={sortBy === col.key} dir={sortDir} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 6 }, (_, i) => <SkeletonRow key={i} />)
              : courses.length === 0
              ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="empty-state">
                    <div className="empty-inner">
                      <p>No results match your filters.</p>
                      <p className="empty-sub">Try adjusting or clearing your filters.</p>
                    </div>
                  </td>
                </tr>
              )
              : courses.map((course) => (
                <tr
                  key={course.id}
                  className={`result-row ${course.priority ? "result-row--priority" : ""} ${course.isActive === false ? "result-row--inactive" : ""}`}
                >
                  <td>
                    {course.priority && (
                      <span className="sales-motion-tag"><span className="fire-icon">🔥</span> SALES MOTION</span>
                    )}
                    <span className={`status-badge status-badge--${course.isActive === false ? "inactive" : "active"}`}>
                      <span className="status-badge-dot" />
                      {course.isActive === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td className="program-name-cell">
                    <strong>{course.programName}</strong>
                    {course.isNew && (
                      <span className="badge-new"><span className="badge-new-dot" />NEW</span>
                    )}
                  </td>
                  <td>
                    {course.practice && (
                      <span className={`tag-text ${PRACTICE_COLOR[course.practice] || ""}`}>{course.practice}</span>
                    )}
                  </td>
                  <td>
                    {course.solutionType && (
                      <span className={`tag-text ${SOLUTION_COLOR[course.solutionType] || ""}`}>{course.solutionType}</span>
                    )}
                  </td>
                  <td><ExpiryCell value={course.expiry} /></td>
                  <td>
                    <DocumentLinks
                      coverEmail={course.coverEmail}
                      approachNote={course.approachNote}
                      successStory={course.successStory}
                      discoveryQuestions={course.discoveryQuestions}
                      modified={course.modified}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
